'use client';

/**
 * ComposerVendorsCard — recipient list + broadcast pseudo-row + accordion.
 *
 * v4 reshape (synthesis from cd-chat_v3.html). Single home for
 * pre-submit messaging:
 *   - Broadcast pseudo-row at top → expanded body is the broadcast
 *     composer (subsumes the prior `composer_quick_chat` card).
 *   - Per-vendor rows below → expandable to per-vendor threaded
 *     composers (placeholder body in this commit; threading lands
 *     next).
 *   - Add-vendor typeahead at the bottom.
 *
 * Accordion: single-open. Expansion state lives in
 * `useCartStore.expandedComposerRow` (`"broadcast" | vendor_id |
 * null`). Per-row draft buffer in `useCartStore.composerDrafts`
 * preserves typed-but-unsent text across mode flips.
 *
 * Each vendor row resolves its phone via `vendor_details` at mount.
 * "No phone" surfaces as a state pill + inline error strip BEFORE
 * Send — buyer fixes the data structurally, not via a post-send toast.
 *
 * Strategies (capability-resolved):
 *   - `(vendor_intelligence, detail)` — per-vendor identity + phone
 *   - `(graph_explore, vendor_list)`  — typeahead source
 *   - `(messaging, send_whatsapp)`    — broadcast + per-vendor send
 *
 * Hides on `rfq_edit` (vendor changes are tender-shape ops, not
 * field edits) and `mcp` (vendor info derives from items there).
 *
 * @see klyde_edge/composition/cards/composer.py — composer_vendors spec
 * @see klyde_edge/ipe-wireframe/index.html        — v4 layout reference
 *
 * @layer presentation
 * @arch_component_type card
 * @arch_domain procurement
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Users,
  X,
  RefreshCw,
  Send,
  Megaphone,
  AlertCircle,
  Loader2,
  CheckCircle,
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useCardExport } from '@/lib/design/card-export';
import { useCartStore } from '@/lib/stores/cart-store';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useDevUserStore } from '@/lib/stores/dev-user-store';
import { useVendors, useStrategyData, useSendWhatsApp, usePostToChatter } from '@/lib/api/hooks';
import { useComposerLabel, useTabLabel, getComposerLabel } from '@/lib/stores/total-matrix-store';
import { api } from '@/lib/api/client';
import { text } from '@/lib/design/tokens';
import { cn } from '@/lib/utils/cn';
import { RailCard } from '@/components/copilot/rail-insights/rail-card';
import { ComposerVendorThread } from './composer-vendor-thread';

const BROADCAST_KEY = 'broadcast';

// Pre-submit broadcast / "Quick Message" framing is disabled — UX call:
// keep the cart focused on RFQ assembly; pre-submit messaging belongs on
// the chat surface, not in the composer stack. Flip to true (or move to a
// matrix-driven flag later) to bring the broadcast row + RFQ title back.
const SHOW_BROADCAST = false;

interface VendorDetail {
  semantic_id: string;
  name: string | null;
  odoo_id: number | null;
  email: string | null;
  phone: string | null;
}

interface BroadcastResult {
  /** Sent and delivered via WhatsApp. */
  whatsapp: number;
  /** Posted as internal note (vendor had no phone — fallback to chatter). */
  chatter: number;
  /** Send/post error. */
  failed: number;
  /** Vendor record has no Odoo id — can't post anywhere. */
  unreachable: number;
}

export function ComposerVendorsCard() {
  // i18n labels — server-driven, lang follows the matrix fetch.
  const labelSyncTitle = useComposerLabel('card_actions_sync_title');
  const labelNoVendors = useComposerLabel('no_vendors_yet');
  const labelAddPlaceholder = useComposerLabel('add_vendor_placeholder');
  const labelAddLoading = useComposerLabel('add_vendor_loading');
  const labelAddNoMore = useComposerLabel('add_vendor_no_more');

  const activeIntent = useCartStore((s) => s.activeIntent);
  const items = useCartStore((s) => s.items);
  const vendorIds = useCartStore((s) => s.vendorIds);
  const vendorNames = useCartStore((s) => s.vendorNames);
  const removeVendor = useCartStore((s) => s.removeVendor);
  const addVendor = useCartStore((s) => s.addVendor);
  const syncVendorsFromItems = useCartStore((s) => s.syncVendorsFromItems);
  const expandedRow = useCartStore((s) => s.expandedComposerRow);
  const setExpandedRow = useCartStore((s) => s.setExpandedComposerRow);

  // Scene-only export — the selected vendor set (cart-store draft).
  // vendorNames is a parallel array to vendorIds.
  useCardExport(useMemo(() => ({
    label: vendorIds.length ? `Vendors (${vendorIds.length})` : 'Vendors',
    content_key: 'composer_vendors',
    surface: 'composer' as const,
    status: 'loaded' as const,
    query_key: null,
    text: vendorIds.length
      ? vendorIds.map((id, i) => vendorNames[i] ?? id).join(', ')
      : 'No vendors selected',
    json: {
      count: vendorIds.length,
      vendors: vendorIds.map((id, i) => ({ vendor_id: id, name: vendorNames[i] ?? null })),
      expanded_row: expandedRow ?? null,
    },
  }), [vendorIds, vendorNames, expandedRow]));

  const vendorListStrategy = useCapabilityStore((s) =>
    s.strategyName('graph_explore', 'vendor_list'),
  );
  const { data: vendorListData, isLoading: vendorListLoading } = useVendors(
    vendorListStrategy ?? '',
    { limit: 100 },
  );

  const [search, setSearch] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);

  // Recipients are derived from the cart's products: every distinct vendor
  // of a cart item is a recipient. Re-sync on any cart change (not just when
  // empty) so adding products from multiple vendors auto-populates them all —
  // not just the first. `syncVendorsFromItems` is idempotent (adds only
  // missing vendors), so this never duplicates; a manually-removed recipient
  // can reappear on a later cart change, which is the accepted tradeoff for
  // "recipients readable from the products."
  useEffect(() => {
    if (items.length > 0) syncVendorsFromItems();
  }, [items, syncVendorsFromItems]);

  // Outside-click closes the dropdown.
  useEffect(() => {
    const handler = () => setShowDropdown(false);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  const filteredAvailable = useMemo(() => {
    const vendors = vendorListData?.vendors ?? [];
    const available = vendors.filter((v) => !vendorIds.includes(v.semantic_id));
    if (!search) return available.slice(0, 10);
    const s = search.toLowerCase();
    return available.filter((v) => v.name.toLowerCase().includes(s)).slice(0, 10);
  }, [vendorListData, search, vendorIds]);

  // Catalogue lines bind their vendor — a vendor backed by a cart line
  // is a FACT, not a choice: no remove affordance (the backend splits
  // lines per vendor and never asks vendor A to quote vendor B's
  // product, so removing a derived recipient is meaningless — and the
  // sync would resurrect it anyway). Recipient CHOICE only exists for
  // vendor-less lines (buyer-wish / generic), which broadcast to every
  // recipient — only then does the add-vendor typeahead render.
  const lineBackedVendorIds = useMemo(
    () => new Set(items.map((i) => i.vendor_id).filter(Boolean)),
    [items],
  );
  const hasVendorlessLines = useMemo(
    () => items.some((i) => !i.vendor_id),
    [items],
  );

  // Hide entirely on intents that don't address vendors.
  if (activeIntent !== 'rfq' && activeIntent !== 'po') return null;

  const toggleRow = (rowKey: string) => {
    setExpandedRow(expandedRow === rowKey ? null : rowKey);
  };

  // Sync action is icon-only — `RefreshCw` already implies derivation,
  // tooltip carries the actual semantic ("auto-fill from cart items").
  // The label exists in `composer_labels` (`card_actions_sync_title`)
  // so the tooltip translates per viewer language.
  const syncAction =
    items.length > 0 ? (
      <button
        onClick={syncVendorsFromItems}
        className={cn(text.muted, 'flex items-center text-primary hover:text-primary/80 transition-colors text-muted-foreground')}
        title={labelSyncTitle}
        aria-label={labelSyncTitle}
      >
        <RefreshCw className="h-3 w-3" />
      </button>
    ) : undefined;

  return (
    <RailCardWithTitle
      activeIntent={activeIntent}
      vendorCount={vendorIds.length}
      icon={Users}
      actions={syncAction}
    >
      <div className="-mx-3 -my-3">
        {/* Broadcast pseudo-row — same row primitive, broadcast scope.
            Only meaningful when 2+ vendors are present; with 1 vendor
            broadcast collapses to the same shape as a per-vendor send.
            Gated by SHOW_BROADCAST (off — broadcast UX disabled). */}
        {SHOW_BROADCAST && vendorIds.length >= 1 && (
          <BroadcastRow
            vendorCount={vendorIds.length}
            expanded={expandedRow === BROADCAST_KEY}
            onToggle={() => toggleRow(BROADCAST_KEY)}
          />
        )}

        {/* Per-vendor rows — non-expandable in this commit; threaded
            chat lands in the next iteration. */}
        {vendorIds.map((id, idx) => (
          <VendorRow
            key={id}
            vendorId={id}
            vendorName={vendorNames[idx]}
            expanded={expandedRow === id}
            onToggle={() => toggleRow(id)}
            onRemove={() => removeVendor(id)}
            removable={!lineBackedVendorIds.has(id)}
          />
        ))}

        {vendorIds.length === 0 && (
          <div className={cn(text.muted, 'px-3 py-3 text-muted-foreground')}>{labelNoVendors}</div>
        )}

        {/* Add-vendor typeahead — only when the cart has a vendor-less
            line (broadcast recipients are a real choice there); for a
            pure catalogue cart the recipient list is derived fact. */}
        {hasVendorlessLines && (
        <div className="px-3 py-2 border-t border-border/60">
          <div className="relative" onClick={(e) => e.stopPropagation()}>
            <input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setShowDropdown(true);
              }}
              onFocus={() => setShowDropdown(true)}
              placeholder={labelAddPlaceholder}
              className={cn(
                text.body,
                'w-full h-8 px-2.5 rounded-md border border-border bg-background',
                'placeholder:text-muted-foreground/60 focus:outline-none focus:ring-1 focus:ring-primary',
              )}
            />
            {showDropdown && (
              <div className="absolute z-50 mt-1 w-full rounded-md border border-border bg-popover shadow-lg">
                <div className="max-h-40 overflow-auto p-1">
                  {vendorListLoading ? (
                    <div className={cn(text.muted, 'px-2 py-1.5 text-muted-foreground')}>{labelAddLoading}</div>
                  ) : filteredAvailable.length === 0 ? (
                    <NoMatchHint search={search} fallback={labelAddNoMore} className={cn(text.muted, 'px-2 py-1.5 text-muted-foreground')} />
                  ) : (
                    filteredAvailable.map((v) => (
                      <button
                        key={v.semantic_id}
                        onClick={() => {
                          addVendor(v.semantic_id, v.name, v.email);
                          setSearch('');
                          setShowDropdown(false);
                        }}
                        className={cn(text.body, 'w-full rounded px-2 py-1.5 text-left hover:bg-accent')}
                      >
                        {v.name}
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
        )}
      </div>
    </RailCardWithTitle>
  );
}

/**
 * Title-localised RailCard wrapper. Title is intent-aware: under RFQ
 * the card surfaces a "Quick Chat" framing (the rail does small +
 * recent messaging); other intents fall back to a count-suffixed
 * "Vendors · N". Both label keys live in `composer_labels`. Hooks
 * are called unconditionally so the parent's render order is stable
 * regardless of which branch lands.
 */
function RailCardWithTitle({
  activeIntent,
  vendorCount,
  icon,
  actions,
  children,
}: {
  activeIntent: string | null;
  vendorCount: number;
  icon: typeof Users;
  actions: React.ReactNode;
  children: React.ReactNode;
}) {
  const titleQuickChat = useComposerLabel('card_title_quick_chat');
  const titleVendorsCount = useComposerLabel('card_title_vendors_count', { count: vendorCount });
  // Title flip to "Quick Chat" is part of the broadcast UX — disabled
  // alongside SHOW_BROADCAST. Always shows the vendor-count framing.
  const title = SHOW_BROADCAST && activeIntent === 'rfq' ? titleQuickChat : titleVendorsCount;
  return (
    <RailCard title={title} icon={icon} actions={actions}>
      {children}
    </RailCard>
  );
}

// ────────────────────────────────────────────────────────────────────
// Broadcast pseudo-row — same row primitive, broadcast scope.
// Expanded body = recipient strip + textarea + channel + send-to-N.
// Send logic mirrors the prior composer_quick_chat (per-vendor phone
// resolution + send_whatsapp serial dispatch).
// ────────────────────────────────────────────────────────────────────

function BroadcastRow({
  vendorCount,
  expanded,
  onToggle,
}: {
  vendorCount: number;
  expanded: boolean;
  onToggle: () => void;
}) {  const labelTitle = useComposerLabel('broadcast_title', { count: vendorCount });
  const labelSubtitle = useComposerLabel('broadcast_subtitle');

  return (
    <div
      className={cn(
        'border-b border-primary/20',
        expanded ? 'bg-primary/[.06]' : 'bg-primary/[.04]',
      )}
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-2.5 px-3 py-2.5 text-left"
      >
        <ChevronRight open={expanded} />
        <span
          className={cn(
            'inline-flex items-center justify-center rounded-md bg-primary text-primary-foreground',
            'h-6 w-6 flex-shrink-0',
          )}
        >
          <Megaphone className="h-3.5 w-3.5" />
        </span>
        <div className="flex-1 min-w-0">
          <div className={cn(text.body, 'font-medium')}>{labelTitle}</div>
          <div className={cn(text.muted, 'text-muted-foreground')}>{labelSubtitle}</div>
        </div>
      </button>

      {expanded && <BroadcastBody />}
    </div>
  );
}

function BroadcastBody() {  const queryClient = useQueryClient();

  // i18n labels — server-driven, lang follows the matrix fetch.
  const labelTextarea = useComposerLabel('broadcast_textarea_placeholder');
  const labelIncludeSummary = useComposerLabel('broadcast_include_summary');
  const labelSending = useComposerLabel('broadcast_sending');
  const labelFooter = useComposerLabel('broadcast_footer');

  const items = useCartStore((s) => s.items);
  const vendorIds = useCartStore((s) => s.vendorIds);
  const vendorNames = useCartStore((s) => s.vendorNames);
  const projectName = useCartStore((s) => s.projectName);
  const requestedDate = useCartStore((s) => s.requestedDate);
  const draft = useCartStore((s) => s.composerDrafts[BROADCAST_KEY] ?? '');
  const setDraft = useCartStore((s) => s.setComposerDraft);
  const clearDraft = useCartStore((s) => s.clearComposerDraft);

  const vendorDetailsStrategy = useCapabilityStore((s) =>
    s.strategyName('vendor_intelligence', 'detail'),
  );
  const sendWhatsAppStrategy = useCapabilityStore((s) =>
    s.strategyName('messaging', 'send_whatsapp'),
  );
  const chatterPostStrategy = useCapabilityStore((s) =>
    s.strategyName('messaging', 'chatter_post'),
  );
  const chatHistoryStrategy = useCapabilityStore((s) =>
    s.strategyName('messaging', 'history'),
  );
  // Canonical mutation hooks — same path the per-vendor thread and
  // the chat panel use. The fan-out loop below calls `mutateAsync`
  // per recipient so cache invalidation, success/error semantics, and
  // any future cross-cutting (rate limiting, retry, observability)
  // wired into the hook layer apply here too.
  const sendWhatsAppMut = useSendWhatsApp(sendWhatsAppStrategy ?? '');
  const postChatterMut = usePostToChatter(chatterPostStrategy ?? '');
  const selectedUser = useDevUserStore((s) => s.selectedUser);

  const [excluded, setExcluded] = useState<Set<string>>(new Set());
  const [includeSummary, setIncludeSummary] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [result, setResult] = useState<BroadcastResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const selectedIds = vendorIds.filter((id) => !excluded.has(id));
  const canSend = selectedIds.length > 0 && draft.trim().length > 0 && !isSending;

  const buildBody = () => {
    if (!includeSummary) return draft;
    const parts = [`${items.length} item${items.length === 1 ? '' : 's'}`];
    if (projectName) parts.push(`for ${projectName}`);
    if (requestedDate) parts.push(`delivery ${requestedDate}`);
    return `${draft}\n\n— ${parts.join(' · ')}`;
  };

  const toggleVendor = (id: string) => {
    const next = new Set(excluded);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setExcluded(next);
  };

  const handleSend = async () => {
    if (!canSend) return;
    if (!vendorDetailsStrategy || !sendWhatsAppStrategy || !chatterPostStrategy) {
      setError(getComposerLabel('strategies_missing'));
      return;
    }
    setIsSending(true);
    setResult(null);
    setError(null);

    const body = buildBody();
    const tally: BroadcastResult = { whatsapp: 0, chatter: 0, failed: 0, unreachable: 0 };

    const detailsResults = await Promise.all(
      selectedIds.map((id) =>
        api
          .getStrategyData<VendorDetail>(vendorDetailsStrategy, { semantic_id: id })
          .then((r) => ({ id, ok: r.success, data: r.data ?? null }))
          .catch(() => ({ id, ok: false, data: null as VendorDetail | null })),
      ),
    );

    // Fan out per-vendor via each vendor's approved channel:
    //   - has phone     → WhatsApp (delivered to vendor)
    //   - no phone but
    //     in Odoo       → Chatter (internal note, logged on partner)
    //   - not in Odoo   → unreachable (skip with a tally entry)
    //
    // Both branches go through the canonical mutation hooks so this
    // path matches the per-vendor thread and the chat panel exactly:
    // same wire shape, same error envelope (the hooks throw on
    // `res.success === false`), same cache invalidation. The fan-out
    // semantics (per-vendor tally, aggregate `isSending`) stay local
    // since one logical "broadcast" is N independent sends.
    for (const r of detailsResults) {
      const odooId = r.data?.odoo_id ?? null;
      const phone = r.data?.phone ?? null;
      if (!r.ok || !odooId) {
        tally.unreachable += 1;
        continue;
      }
      try {
        if (phone) {
          const sendRes = await sendWhatsAppMut.mutateAsync({
            to: phone,
            message: body,
            context_model: 'res.partner',
            context_id: odooId,
            author_name: selectedUser?.name,
          });
          if (sendRes?.whatsapp_sent) tally.whatsapp += 1;
          else tally.failed += 1;
        } else {
          const postRes = await postChatterMut.mutateAsync({
            message: body,
            context_model: 'res.partner',
            context_id: odooId,
          });
          if (postRes?.success) tally.chatter += 1;
          else tally.failed += 1;
        }
      } catch {
        tally.failed += 1;
      }
    }

    setIsSending(false);
    setResult(tally);
    if (tally.failed === 0 && tally.unreachable === 0) {
      clearDraft(BROADCAST_KEY);
      const parts: string[] = [];
      if (tally.whatsapp > 0) parts.push(getComposerLabel('broadcast_n_via_whatsapp', { count: tally.whatsapp }));
      if (tally.chatter > 0) parts.push(getComposerLabel('broadcast_n_as_note', { count: tally.chatter }));
      toast.success(getComposerLabel('broadcast_sent_toast', { summary: parts.join(', ') }));
    }

    // Invalidate per-vendor chat history caches for the recipients.
    // The mutation hooks (`useSendWhatsApp` / `usePostToChatter`) only
    // invalidate the legacy `['project-messages']` queryKey, which
    // misses the unified-chat queryKey shape (`['strategy-data',
    // chatStrategy, ...]`). Without this nudge, a buyer expanding a
    // vendor row right after a broadcast would see stale (pre-send)
    // history until the 15-min staleTime expires.
    //
    // Predicate matches any chat-history query whose params reference
    // a recipient vendor — covers `entity_type='vendor'` reads from
    // both the composer rail thread and the chat panel.
    if (chatHistoryStrategy) {
      const recipientSet = new Set(selectedIds);
      queryClient.invalidateQueries({
        predicate: (q) => {
          const k = q.queryKey;
          if (k[0] !== 'strategy-data' || k[1] !== chatHistoryStrategy) return false;
          const params = k[2] as { entity_type?: string; entity_sid?: string } | undefined;
          return params?.entity_type === 'vendor' && !!params.entity_sid && recipientSet.has(params.entity_sid);
        },
      });
    }
  };

  return (
    <div className="px-3 pb-3 space-y-2">
      {/* Recipient strip — chips with toggleable inclusion */}
      <div className="flex flex-wrap gap-1 p-2 rounded-md bg-background border border-border">
        {vendorIds.map((id, idx) => {
          const off = excluded.has(id);
          return (
            <button
              key={id}
              type="button"
              onClick={() => toggleVendor(id)}
              className={cn(
                text.body,
                'inline-flex items-center px-2 py-0.5 rounded-full border transition-colors',
                off
                  ? 'bg-muted text-muted-foreground border-border line-through opacity-65'
                  : 'bg-primary/10 text-primary border-primary/25 hover:bg-primary/15',
              )}
            >
              {vendorNames[idx]}
            </button>
          );
        })}
      </div>

      <textarea
        value={draft}
        onChange={(e) => setDraft(BROADCAST_KEY, e.target.value)}
        placeholder={labelTextarea}
        className={cn(
          text.body,
          'w-full h-20 px-2 py-1.5 rounded-md border border-border bg-background',
          'placeholder:text-muted-foreground/60 resize-none focus:outline-none focus:ring-1 focus:ring-primary',
        )}
      />

      <div className="flex items-center justify-between gap-2">
        <label className={cn(text.muted, 'flex items-center gap-1.5 cursor-pointer')}>
          <input
            type="checkbox"
            checked={includeSummary}
            onChange={(e) => setIncludeSummary(e.target.checked)}
            className="h-3 w-3"
          />
          {labelIncludeSummary}
        </label>
        <BroadcastSendButton
          isSending={isSending}
          canSend={canSend}
          recipientCount={selectedIds.length}
          onClick={handleSend}
          sendingLabel={labelSending}
        />
      </div>

      <p className={cn(text.muted, 'italic')}>{labelFooter}</p>

      {error && (
        <div className="rounded-md p-2 flex items-start gap-2 bg-destructive/10 text-destructive">
          <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <p className={text.body}>{error}</p>
        </div>
      )}

      {result && !error && (
        <div
          className={cn(
            'rounded-md p-2 flex items-start gap-2',
            result.failed === 0 && result.unreachable === 0
              ? 'bg-green-500/10 text-green-600 dark:text-green-400'
              : 'bg-yellow-500/10 text-yellow-700 dark:text-yellow-400',
          )}
        >
          <CheckCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <BroadcastResultBody result={result} totalSelected={selectedIds.length} />
        </div>
      )}
    </div>
  );
}

/**
 * "Send to N" / "Sending…" — lifted to a subcomponent so the
 * `{count}`-templated label is computed unconditionally even when
 * `isSending` is true.
 */
function BroadcastSendButton({
  isSending,
  canSend,
  recipientCount,
  onClick,
  sendingLabel,
}: {
  isSending: boolean;
  canSend: boolean;
  recipientCount: number;
  onClick: () => void;
  sendingLabel: string;
}) {  const sendLabel = useComposerLabel('broadcast_send_to_n', { count: recipientCount });
  return (
    <button
      onClick={onClick}
      disabled={!canSend}
      className={cn(
        text.body,
        'font-medium px-3 py-1.5 rounded-md flex items-center gap-1.5 transition-colors',
        'bg-primary text-primary-foreground hover:bg-primary/90',
        'disabled:opacity-50 disabled:cursor-not-allowed',
      )}
    >
      {isSending ? (
        <>
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          {sendingLabel}
        </>
      ) : (
        <>
          <Send className="h-3.5 w-3.5" />
          {sendLabel}
        </>
      )}
    </button>
  );
}

/**
 * Broadcast result — headline + per-channel breakdown. Hooks-rules
 * safe because the parent gates on `result && !error`; inside this
 * component all hooks are unconditional.
 */
function BroadcastResultBody({
  result,
  totalSelected,
}: {
  result: BroadcastResult;
  totalSelected: number;
}) {  const total = result.whatsapp + result.chatter;
  const headline = useComposerLabel(
    totalSelected === 1 ? 'broadcast_sent_to_n_of_m_one' : 'broadcast_sent_to_n_of_m',
    { sent: total, total: totalSelected },
  );
  const nothingSent = useComposerLabel('broadcast_nothing_sent');
  const partWhatsApp = useComposerLabel('broadcast_n_via_whatsapp', { count: result.whatsapp });
  const partChatter = useComposerLabel('broadcast_n_as_note', { count: result.chatter });
  const partFailed = useComposerLabel('broadcast_n_failed', { count: result.failed });
  const partUnreachable = useComposerLabel('broadcast_n_unreachable', { count: result.unreachable });
  const detail = [
    result.whatsapp > 0 ? partWhatsApp : null,
    result.chatter > 0 ? partChatter : null,
    result.failed > 0 ? partFailed : null,
    result.unreachable > 0 ? partUnreachable : null,
  ].filter(Boolean).join(' · ');
  return (
    <div className="min-w-0">
      <p className={text.body}>{total > 0 ? headline : nothingSent}</p>
      <p className={text.muted}>{detail}</p>
    </div>
  );
}

/**
 * "No vendors matching X" / "No more vendors" — `{search}`-templated,
 * hooks-safe (always renders one or the other).
 */
function NoMatchHint({
  search,
  fallback,
  className,
}: {
  search: string;
  fallback: string;
  className?: string;
}) {
  const matchLabel = useComposerLabel('add_vendor_no_match', { search });
  return <div className={className}>{search ? matchLabel : fallback}</div>;
}

// ────────────────────────────────────────────────────────────────────
// Per-vendor row — fetches vendor_details for state pill + channel.
// Body is a placeholder in this commit; threading lands next.
// ────────────────────────────────────────────────────────────────────

function VendorRow({
  vendorId,
  vendorName,
  expanded,
  onToggle,
  onRemove,
  removable,
}: {
  vendorId: string;
  vendorName: string;
  expanded: boolean;
  onToggle: () => void;
  onRemove: () => void;
  /** Only manually-added recipients (no backing cart line) can be
   *  removed; line-backed vendors are derived facts. */
  removable: boolean;
}) {
  // i18n labels.
  const labelLoading = useComposerLabel('vendor_loading');
  const labelChannelWA = useComposerLabel('channel_wa_short');
  const labelChannelEmail = useComposerLabel('channel_email_short');
  const labelChannelNone = useComposerLabel('channel_none_short');
  const labelRemoveTitle = useComposerLabel('vendor_remove_title');
  const labelNoPhoneError = useComposerLabel('vendor_no_phone_error');
  const labelAddNow = useComposerLabel('vendor_no_phone_add_now');

  const vendorDetailsStrategy = useCapabilityStore((s) =>
    s.strategyName('vendor_intelligence', 'detail'),
  );
  const { data, isLoading } = useStrategyData<VendorDetail>(
    vendorDetailsStrategy ?? '',
    { semantic_id: vendorId },
    !!vendorDetailsStrategy,
  );

  const phone = data?.data?.phone ?? null;
  const email = data?.data?.email ?? null;
  const noPhone = !isLoading && !phone;
  const channelLabel = phone ? labelChannelWA : email ? labelChannelEmail : labelChannelNone;

  const initial = vendorName.charAt(0).toUpperCase();
  const avatarBg = avatarColor(initial);

  const handleRemoveClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onRemove();
    },
    [onRemove],
  );

  return (
    <div className={cn('border-b border-border/60', expanded && 'bg-card-muted')}>
      {/* Row head — two siblings (expander + remove). The expander
          is the only clickable thing in the row chrome; the remove
          button stands on its own (no nesting, no stopPropagation
          gymnastics). */}
      <div className="flex items-center hover:bg-muted/40 transition-colors">
        <div
          role="button"
          tabIndex={0}
          onClick={onToggle}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onToggle();
            }
          }}
          className="flex-1 min-w-0 flex items-center gap-2.5 px-3 py-2.5 cursor-pointer"
        >
          <ChevronRight open={expanded} />
          <span
            className="inline-flex items-center justify-center rounded-full text-white font-semibold h-6 w-6 flex-shrink-0 text-xs"
            style={{ background: avatarBg }}
          >
            {initial}
          </span>
          <div className="flex-1 min-w-0">
            <div className={cn(text.title, 'truncate')}>{vendorName}</div>
            <div className={cn(text.muted, 'truncate')}>
              <span className={cn(text.muted, 'inline-block px-1.5 py-0.5 rounded bg-muted mr-1.5 align-middle')}>
                {channelLabel}
              </span>
              {isLoading ? labelLoading : (email ?? phone ?? null)}
            </div>
          </div>
        </div>
        {removable && (
          <button
            type="button"
            onClick={handleRemoveClick}
            className="text-muted-foreground hover:text-destructive transition-colors px-3 py-2.5 flex-shrink-0"
            title={labelRemoveTitle}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {/* Inline error strip — pre-send, structural. "add now" expands the
          row so the in-thread phone-input affordance is reachable. */}
      {noPhone && !expanded && (
        <div className="ml-12 mr-3 mb-2 rounded-md bg-yellow-500/10 text-yellow-700 dark:text-yellow-400 px-2 py-1.5 flex items-center gap-1.5 text-xs">
          <AlertCircle className="h-3 w-3 flex-shrink-0" />
          <span className="flex-1">{labelNoPhoneError}</span>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onToggle();
            }}
            className="underline hover:no-underline cursor-pointer"
          >
            {labelAddNow}
          </button>
        </div>
      )}

      {/* Expanded body — threaded chat scoped to this vendor.
          Reads via the unified `chat` strategy (resolved from the
          capability matrix as `(messaging, history)` with
          `entity_type='vendor'`), sends via send_whatsapp to the
          vendor's res.partner chatter. The shared messaging gateway
          routes inbound replies to the same chatter, so a refetch
          after send picks them up. */}
      {expanded && <ExpandedThreadBody vendorId={vendorId} vendorName={vendorName} />}
    </div>
  );
}

function ExpandedThreadBody({
  vendorId,
  vendorName,
}: {
  vendorId: string;
  vendorName: string;
}) {
  const items = useCartStore((s) => s.items);
  const projectName = useCartStore((s) => s.projectName);
  const vendorDetailsStrategy = useCapabilityStore((s) =>
    s.strategyName('vendor_intelligence', 'detail'),
  );
  const { data } = useStrategyData<VendorDetail>(
    vendorDetailsStrategy ?? '',
    { semantic_id: vendorId },
    !!vendorDetailsStrategy,
  );

  return (
    <ComposerVendorThread
      vendorId={vendorId}
      vendorName={vendorName}
      vendorPhone={data?.data?.phone ?? null}
      vendorOdooId={data?.data?.odoo_id ?? null}
      itemCount={items.length}
      projectName={projectName}
    />
  );
}

// ────────────────────────────────────────────────────────────────────
// Small helpers
// ────────────────────────────────────────────────────────────────────

function ChevronRight({ open }: { open: boolean }) {
  return (
    <svg
      className={cn('h-3 w-3 text-muted-foreground transition-transform flex-shrink-0', open && 'rotate-90')}
      viewBox="0 0 12 12"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4 2 L8 6 L4 10" />
    </svg>
  );
}

const AVATAR_PALETTE: Record<string, string> = {
  A: '#7a4a8c', B: '#3a6b5a', C: '#8c5a3a', D: '#3a5a8c', E: '#8c3a4a',
  F: '#5a7a3a', G: '#3a8c7a', H: '#5a3a8c', I: '#8c7a3a', J: '#3a8c4a',
  K: '#4a3a8c', L: '#8c3a8c', M: '#7a4a8c', N: '#3a8c7a', O: '#8c5a3a',
  P: '#3a8c7a', Q: '#3a5a8c', R: '#8c4a4a', S: '#3a8c4a', T: '#8c6a3a',
  U: '#5a8c3a', V: '#7a3a8c', W: '#3a7a8c', X: '#8c3a7a', Y: '#8c3a3a',
  Z: '#3a3a8c',
};
function avatarColor(initial: string): string {
  return AVATAR_PALETTE[initial] || '#5a5a5a';
}
